import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { User, Camera, Save, ShieldCheck, BadgeCheck, CalendarDays, Crosshair, Copy, Download, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail, safeCopy } from "@/lib/api";
import { FirstDayCard } from "@/components/hub/FirstDayCard";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

const EASE = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2";

const ROLE_META = {
  admin: { label: "Studio Admin", cls: "text-nc-orange border-nc-orange/40 bg-nc-orange/10" },
  dev: { label: "Developer", cls: "text-nc-blue border-nc-blue/40 bg-nc-blue/10" },
  citizen: { label: "Citizen", cls: "text-nc-green border-nc-green/40 bg-nc-green/10" },
  sales: { label: "Sales Rep", cls: "text-nc-blue border-nc-blue/40 bg-nc-blue/10" },
  client: { label: "Client", cls: "text-nc-green border-nc-green/40 bg-nc-green/10" },
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const stored = (() => { try { return JSON.parse(localStorage.getItem("nc_user")); } catch { return null; } })();

  useEffect(() => {
    if (!stored) { navigate("/auth"); return; }
    api.get("/auth/me")
      .then((r) => {
        setProfile(r.data);
        setName(r.data.name || "");
        setBio(r.data.bio || "");
      })
      .catch(() => { localStorage.removeItem("nc_token"); navigate("/auth"); });
    api.get("/recruitment/wallet").then((r) => setWallet(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/profile", { name, bio });
      const updated = { ...stored, name: r.data.name };
      localStorage.setItem("nc_user", JSON.stringify(updated));
      setProfile((p) => ({ ...p, name: r.data.name, bio: r.data.bio }));
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, avatar_path: r.data.avatar_path, avatar_ts: Date.now() }));
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadData = async () => {
    try {
      const r = await api.get("/account/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `specpixel-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data export has downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed — try again");
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete", { password: delPassword, confirm: delConfirm });
      localStorage.removeItem("nc_token");
      localStorage.removeItem("nc_user");
      toast.success("Account permanently deleted. Sorry to see you go.");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Deletion failed — try again");
    } finally {
      setDeleting(false);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase animate-pulse">Loading profile...</p>
      </div>
    );
  }

  const role = ROLE_META[profile.role] || ROLE_META.citizen;
  const avatarUrl = profile.avatar_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${profile.id}${profile.avatar_ts ? `?t=${profile.avatar_ts}` : ""}`
    : null;

  return (
    <div data-testid="profile-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-3">Citizen Records // Identity</p>
          <h1 data-testid="profile-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-4xl text-zinc-50">Your Profile</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 w-full grid grid-cols-1 lg:grid-cols-3 gap-8">
        <motion.section data-testid="profile-card" className="border border-white/10 bg-black/40 p-6 flex flex-col items-center text-center gap-4 h-fit" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
          <div className="relative group">
            {avatarUrl ? (
              <img data-testid="profile-avatar" src={avatarUrl} alt={profile.name} className="w-32 h-32 rounded-full object-cover border-2 border-nc-blue/50" />
            ) : (
              <div data-testid="profile-avatar-placeholder" className="w-32 h-32 rounded-full border-2 border-white/15 bg-nc-blue/10 flex items-center justify-center">
                <User className="w-12 h-12 text-nc-blue" />
              </div>
            )}
            <button
              data-testid="profile-avatar-upload-button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 w-9 h-9 bg-nc-orange hover:bg-nc-orange/80 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
              title="Upload avatar"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input ref={fileRef} data-testid="profile-avatar-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} />
          </div>
          <div>
            <p data-testid="profile-display-name" className="font-sans font-bold uppercase text-xl text-zinc-50">{profile.name || "—"}</p>
            <p className="font-mono text-xs text-zinc-500 mt-1">{profile.email}</p>
          </div>
          <span data-testid="profile-role-badge" className={`font-mono text-[10px] tracking-[0.25em] uppercase border px-3 py-1.5 flex items-center gap-1.5 ${role.cls}`}>
            <ShieldCheck className="w-3.5 h-3.5" /> {role.label}
          </span>
          {uploading && <p className="font-mono text-[10px] text-zinc-500 animate-pulse">Uploading avatar…</p>}
        </motion.section>

        <motion.section className="lg:col-span-2 flex flex-col gap-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1, ease: EASE }}>
          <FirstDayCard />
          <div className="border border-white/10 bg-black/40 p-6 flex flex-col gap-5">
            <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
              <BadgeCheck className="w-4 h-4 text-nc-blue" /> Identity Details
            </h2>
            <div>
              <label className={labelCls}>Display name</label>
              <input data-testid="profile-name-input" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} minLength={2} maxLength={24} />
            </div>
            <div>
              <label className={labelCls}>Bio</label>
              <textarea data-testid="profile-bio-input" rows={4} maxLength={500} className={`${inputCls} resize-none`} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell the studio who you are — role, skills, what you're working on…" />
              <p className="font-mono text-[9px] text-zinc-600 mt-1.5 text-right">{bio.length} / 500</p>
            </div>
            <button data-testid="profile-save-button" onClick={save} disabled={saving || name.trim().length < 2} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Profile"}
            </button>
          </div>

          <div className="border border-white/10 bg-black/40 p-6">
            <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2 mb-4">
              <CalendarDays className="w-4 h-4 text-nc-orange" /> Account
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-600 uppercase">Email</p>
                <p data-testid="profile-email" className="font-mono text-sm text-zinc-200 mt-1">{profile.email}</p>
              </div>
              <div>
                <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-600 uppercase">Access level</p>
                <p className="font-mono text-sm text-zinc-200 mt-1">{role.label}</p>
              </div>
            </div>
            {(profile.role === "dev" || profile.role === "admin") && (
              <p className="font-mono text-[10px] text-zinc-500 mt-4 leading-relaxed border-t border-white/10 pt-4">
                You have Dev Hub access — internal UE5 documentation and the Plastic checkin feed are available from the nav bar.
              </p>
            )}
          </div>

          {wallet && (
            <div data-testid="bounty-wallet" className="border border-nc-orange/30 bg-nc-orange/5 p-6">
              <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2 mb-4">
                <Crosshair className="w-4 h-4 text-nc-orange" /> Referral Earnings
              </h2>
              <p className="font-mono text-[10px] text-zinc-400 leading-relaxed mb-4">
                Share your referral link. When someone you refer completes a project, you earn {wallet.bounty_pct}% of the platform service fee as commission.
              </p>
              <div className="border border-white/10 bg-[#050505]/70 px-4 py-3 flex items-center justify-between gap-3 mb-4">
                <div>
                  <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-600 uppercase">Your referral link</p>
                  <p data-testid="recruitment-id" className="font-mono text-[11px] text-nc-orange mt-1 break-all">{window.location.origin}/auth?rec={wallet.recruitment_id}</p>
                </div>
                <button data-testid="copy-recruitment-link" onClick={async () => { (await safeCopy(`${window.location.origin}/auth?rec=${wallet.recruitment_id}`)) ? toast.success("Referral link copied") : toast.error("Copy blocked — copy the link manually"); }} className="text-zinc-400 hover:text-white shrink-0 transition-colors"><Copy className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[["Referrals", wallet.recruits], ["Projects completed", wallet.completed_projects], ["Commission earned", `AUD ${wallet.total_earned.toFixed(2)}`]].map(([l, v]) => (
                  <div key={l} className="border border-white/10 bg-black/40 px-3 py-2.5 text-center">
                    <p className="font-sans font-bold text-lg text-zinc-50">{v}</p>
                    <p className="font-mono text-[8px] tracking-[0.2em] text-zinc-500 uppercase mt-0.5">{l}</p>
                  </div>
                ))}
              </div>
              {wallet.entries.slice(0, 5).map((e, i) => (
                <div key={e.id} data-testid={`bounty-entry-${i + 1}`} className="flex justify-between font-mono text-[10px] border-b border-white/5 py-2">
                  <span className="text-zinc-400 truncate">{e.recruit_email}</span>
                  <span className="text-nc-green shrink-0">+ AUD {e.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          <div data-testid="privacy-data-section" className="border border-white/10 bg-black/40 p-6">
            <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-nc-green" /> Privacy &amp; Data
            </h2>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed mb-4">
              You own your data. Read the{" "}
              <Link to="/privacy" data-testid="profile-privacy-link" className="text-nc-blue underline underline-offset-2">Privacy Policy</Link> — it covers what we collect
              (including device information used for security), why, how long it's kept, and who it's shared with.
            </p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="download-data-button" onClick={downloadData}
                className="border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2.5 flex items-center gap-2 transition-colors">
                <Download className="w-3.5 h-3.5" /> Download my data
              </button>
              <button data-testid="delete-account-button" onClick={() => setDelOpen(true)}
                className="border border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2.5 flex items-center gap-2 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Delete my account
              </button>
            </div>
          </div>

          {delOpen && (
            <div data-testid="delete-confirm-modal" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
              <div className="w-full max-w-md border border-red-500/40 bg-[#0a0a0a] p-6 flex flex-col gap-4">
                <h3 className="font-sans font-bold uppercase tracking-widest text-sm text-red-400">Permanently delete your account?</h3>
                <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
                  This immediately and irreversibly wipes: your profile and login, game characters and bank balances on every server,
                  phone messages and chat logs, transaction history, NCDB records, device fingerprints and security data.
                  Only legally-required tax records and an anonymised deletion log remain. <b className="text-red-300">This cannot be undone.</b>
                </p>
                <div>
                  <label className={labelCls}>Password</label>
                  <input data-testid="delete-password-input" type="password" value={delPassword} onChange={(e) => setDelPassword(e.target.value)} className={inputCls} placeholder="Your current password" />
                </div>
                <div>
                  <label className={labelCls}>Type DELETE to confirm</label>
                  <input data-testid="delete-confirm-input" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} className={inputCls} placeholder="DELETE" />
                </div>
                <div className="flex gap-2">
                  <button data-testid="delete-confirm-button" onClick={deleteAccount} disabled={deleting || delConfirm !== "DELETE" || !delPassword}
                    className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-mono text-[10px] font-bold uppercase tracking-[0.2em] py-3 transition-colors">
                    {deleting ? "Wiping…" : "Delete everything forever"}
                  </button>
                  <button data-testid="delete-cancel-button" onClick={() => { setDelOpen(false); setDelPassword(""); setDelConfirm(""); }}
                    className="border border-white/20 text-zinc-300 font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-3 transition-colors">
                    Keep my account
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.section>
      </main>

      <SiteFooter />
    </div>
  );
}
